import { Box, Spinner, useToast, Text } from '@chakra-ui/react';
import { HomeDashboard } from '../../applications/components/dashboard.component';
import { AppDetailsBlueCheck } from './app-details-bluecheck.component';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getAppActiveStep } from '../../../redux/appBuilder/stepperDetails/selectors';
import { useEffect, useState } from 'react';
import {
  BlueCheckApplication,
  BlueCheckApplicationResponse,
} from '../../../utils/types';
import { updateAppActiveStep } from '../../../redux/appBuilder/stepperDetails';
import { ConfigureCallBackUri } from './configure-callbackUri.component';
import {
  createBlueCheckApplication,
  getApplicationById,
  updateBlueCheckApplication,
} from '../../../utils/bluecheck-applications/fetch-bluecheck-applications';
import { IntegrationDetailsScreen } from '../../bluecheck-integration-details/bluecheck-integration-details.screen';
import { useNavigate, useParams } from 'react-router-dom';

export const CreateBlueCheckApp = () => {
  const activeStep = useAppSelector(getAppActiveStep);
  const dispatch = useAppDispatch();
  const [editedApp, setEditeApp] = useState<BlueCheckApplication | null>(null);
  const toast = useToast();
  const [createdApp, setCreatedApp] = useState<BlueCheckApplication>();
  const { appId } = useParams();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (appId) {
      const fetchAppbyId = async () => {
        setLoading(true);
        const data = await getApplicationById(appId as string);
        setEditeApp(data);
        setLoading(false);
      };
      fetchAppbyId();
    }
  }, []);
  const [formData, setFormData] = useState({
    metadataName: '',
    callbackUri: '',
    origins: [] as string[],
  });

  const handleUpdate = (data: any) => {
    setFormData((prev) => {
      return { ...prev, [Object.keys(data)[0]]: Object.values(data)[0] };
    });
  };

  const handleNext = () => {
    dispatch(updateAppActiveStep(activeStep + 1));
  };

  const handleBack = () => {
    dispatch(updateAppActiveStep(activeStep - 1));
  };

  const handleFinish = async () => {
    try {
      if (editedApp) {
        const updatePayload = {
          metadataName: formData.metadataName,
          redirectUris: '',
          origins: formData.origins,
        };
        await updateBlueCheckApplication(editedApp.id, updatePayload);
        toast({
          title: 'Application Updated.',
          description: 'Your application has been updated successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        navigate(`/my-applications/bluecheck-code-preview/${editedApp.id}`);
      } else {
        const response = await createBlueCheckApplication({
          metadataName: formData.metadataName,
          callbackUri: '',
          origins: formData.origins,
        });

        if (!response?.ok) {
          throw new Error('Failed to Create Application');
        }
        const data = (await response.json()) as BlueCheckApplicationResponse;
        setCreatedApp(data.data);
        toast({
          title: 'Application Created.',
          description: 'Your application has been created successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }

      dispatch(updateAppActiveStep(activeStep + 1));
    } catch (error) {
      toast({
        title: 'Error.',
        description: 'Failed to create application.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" mt={6}>
        <Spinner size="lg" />
        <Text mt={4}>Loading Application...</Text>
      </Box>
    );
  }

  return (
    <Box display="flex" h="fit-content" justifyContent="center" width="100%">
      {activeStep === 0 && <HomeDashboard />}
      {activeStep === 1 && (
        <AppDetailsBlueCheck
          editedApp={editedApp}
          onUpdate={(metadataName: string) => handleUpdate({ metadataName })}
          handleNext={handleNext}
        />
      )}

      {activeStep === 2 && (
        <ConfigureCallBackUri
          handleBack={handleBack}
          handleFinish={handleFinish}
          onUpdateUri={(callbackUri: string) => handleUpdate({ callbackUri })}
          onUpdateOrigin={(origins: string[]) => handleUpdate({ origins })}
          editedApp={editedApp}
        />
      )}

      {activeStep === 3 && (
        <IntegrationDetailsScreen createdApp={createdApp ? createdApp : null} />
      )}
    </Box>
  );
};
